import React, { useState, useEffect } from "react";

/**
 * Returns the current open/closed state for the workshop.
 * Schedule (Europe/Budapest):
 *   Mon–Fri  08:00 – 17:00  OPEN
 *   Sat–Sun  closed
 */
function computeStatus(now = new Date()) {
  // Convert to Europe/Budapest local time using Intl
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Budapest",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(now);

  const parts = Object.fromEntries(fmt.map((p) => [p.type, p.value]));
  // weekday is e.g. "Mon", "Tue", ...
  const dayMap = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 0 };
  const dow = dayMap[parts.weekday] ?? 0;
  const hour = parseInt(parts.hour, 10);
  const minute = parseInt(parts.minute, 10);
  const minutes = hour * 60 + minute;

  const OPEN_MIN = 8 * 60;
  const CLOSE_MIN = 17 * 60;

  const isWeekday = dow >= 1 && dow <= 5;
  const isOpen = isWeekday && minutes >= OPEN_MIN && minutes < CLOSE_MIN;

  if (isOpen) {
    return {
      open: true,
      label: "Most nyitva",
      detail: "17:00-ig",
    };
  }

  // Compute next opening
  let nextDayLabel;
  if (isWeekday && minutes < OPEN_MIN) {
    nextDayLabel = "ma 8:00-tól";
  } else if (dow === 5 && minutes >= CLOSE_MIN) {
    nextDayLabel = "hétfőn 8:00-tól";
  } else if (dow === 6) {
    nextDayLabel = "hétfőn 8:00-tól";
  } else if (dow === 0) {
    nextDayLabel = "hétfőn 8:00-tól";
  } else {
    nextDayLabel = "holnap 8:00-tól";
  }

  return {
    open: false,
    label: "Zárva",
    detail: nextDayLabel,
  };
}

export default function OpenStatus({ compact = false }) {
  const [status, setStatus] = useState(() => computeStatus());

  useEffect(() => {
    const tick = () => setStatus(computeStatus());
    // Refresh every minute
    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, []);

  const dotColor = status.open ? "bg-emerald-500" : "bg-zinc-500";
  const ringColor = status.open ? "bg-emerald-500/60" : "bg-zinc-500/0";
  const textColor = status.open ? "text-emerald-400" : "text-zinc-400";

  if (compact) {
    // Just a coloured dot — used inside tight spaces (mobile)
    return (
      <span
        className="relative inline-flex w-2 h-2"
        title={`${status.label} — ${status.detail}`}
        aria-label={`${status.label} — ${status.detail}`}
        data-testid="open-status-dot"
      >
        {status.open && (
          <span
            className={`absolute inline-flex h-full w-full rounded-full ${ringColor} animate-ping`}
          />
        )}
        <span className={`relative inline-flex rounded-full h-2 w-2 ${dotColor}`} />
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1.5 text-[0.65rem] font-medium leading-none whitespace-nowrap"
      data-testid="open-status-label"
    >
      <span className="relative inline-flex w-2 h-2">
        {status.open && (
          <span
            className={`absolute inline-flex h-full w-full rounded-full ${ringColor} animate-ping`}
          />
        )}
        <span
          className={`relative inline-flex rounded-full h-2 w-2 ${dotColor}`}
        />
      </span>
      <span className={textColor}>
        {status.label}
        <span className="text-zinc-500"> — {status.detail}</span>
      </span>
    </span>
  );
}
